<template>
  <div class="filters-content" v-if="filters">
    <template v-for="(filter, index) in filters" :key="index">
      <div v-if="filter.type === 'input'" class="filter-group">
        <el-text tag="b" class="filter-label">{{ filter.label }}</el-text>
        <el-input
          v-model="filterValues[filter.code]"
          :placeholder="filter.placeholder"
          clearable
        />
      </div>

      <div v-else-if="filter.type === 'datetime'" class="filter-group">
        <el-text tag="b" class="filter-label">{{ filter.label }}</el-text>
        <Datepicker
          :is24="false"
          selectText="Valider"
          cancelText="Annuler"
          :placeholder="filter.placeholder"
          format="dd/MM/yyyy"
          v-model="filterValues[filter.code]"
          range
          :presetRanges="presetRanges"
        />
      </div>

      <div v-else-if="filter.type === 'select' && filter.options && filter.options.length > 1" class="filter-group">
        <el-text tag="b" class="filter-label">{{ filter.label }}</el-text>
        <el-select
          v-model="filterValues[filter.code]"
          :placeholder="filter.placeholder"
          clearable
        >
          <el-option
            v-for="(option, key) in filter.options"
            :key="key"
            :label="option.label || option.name || option.text"
            :value="option.value"
          />
        </el-select>
      </div>

      <div v-else-if="filter.type === 'multiselect' && filter.options && filter.options.length > 1" class="filter-group">
        <el-text tag="b" class="filter-label">{{ filter.label }}</el-text>
        <el-select
          v-model="filterValues[filter.code]"
          multiple
          :placeholder="filter.placeholder"
          collapse-tags
          collapse-tags-tooltip
        >
          <el-option
            v-for="option in filter.options"
            :key="option.value"
            :label="option.text || option.label || option.name"
            :value="option.value || option.id"
          />
        </el-select>
      </div>

      <div v-else-if="filter.type === 'checkbox'" class="filter-group">
        <el-tooltip
          v-if="filter.tooltip"
          effect="light"
          :content="filter.tooltip"
          placement="right"
        >
          <el-checkbox
            v-model="filterValues[filter.code]"
            class="d-flex justify-content-center el-button"
          >
            {{ filter.placeholder }}
          </el-checkbox>
        </el-tooltip>
        <el-checkbox
          v-else
          v-model="filterValues[filter.code]"
          class="d-flex justify-content-center el-button"
        >
          {{ filter.placeholder }}
        </el-checkbox>
      </div>
    </template>

    <div class="filter-group">
      <el-button
        @click="resetAllFilters"
        class="d-flex justify-content-center"
      >
        Réinitialiser les filtres
      </el-button>
    </div>
  </div>
</template>

<script>
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { endOfMonth, endOfYear, startOfMonth, startOfYear, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { buildContractFilter } from '@/utils/contractFilterUtils';
import { buildSqlFilters } from '@/utils/simulationFilterUtils';

export default {
  name: 'FilterBuilder',
  components: { Datepicker },
  props: {
    filters: {
      type: Array,
      required: true,
    },
    filterType: {
      type: String,
      default: 'simulation',
      validator: (value) => ['simulation', 'contract'].includes(value)
    }
  },
  emits: ['filtersChanged'],
  data() {
    return {
      filterValues: {},
      locale: fr,
      debounceTimer: null,
      presetRanges: [
        { label: 'Aujourd\'hui', range: [new Date(), new Date()] },
        { label: 'Ce mois', range: [startOfMonth(new Date()), endOfMonth(new Date())] },
        {
          label: 'Le mois dernier',
          range: [startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1))],
        },
        { label: 'Cette année', range: [startOfYear(new Date()), endOfYear(new Date())] },
      ],
    };
  },
  created() {
    if (this.filters) {
      this.initializeFilterValues();
    }
  },
  mounted() {
    if (this.$route.query.filter && this.filters) {
      const statusFilter = this.filters.find(f => f.type === 'multiselect' && f.code === 'status');
      if (statusFilter) {
        this.filterValues[statusFilter.code] = this.$route.query.filter.split(' or ');
      }
    }
  },
  methods: {
    initializeFilterValues() {
      if (!this.filters) return;
      
      this.filters.forEach(filter => {
        const { code, type } = filter;
        
        if (type === 'input' || type === 'select') {
          this.filterValues[code] = '';
        } else if (type === 'multiselect') {
          this.filterValues[code] = [];
        } else if (type === 'datetime') {
          this.filterValues[code] = [];
        } else if (type === 'checkbox') {
          this.filterValues[code] = true;
        }
      });
    },
    resetAllFilters() {
      this.initializeFilterValues();
      
      history.pushState({}, null, this.$route.path);
      
      this.$nextTick(() => {
        this.emitFilters();
      });
    },
    emitFilters() {
      if (this.filterType === 'contract') {
        const contractFilters = buildContractFilter({
          referenceFilter: this.filterValues['reference'] || '',
          identifierFilter: this.filterValues['identifier'] || '',
          reconductionFilter: this.filterValues['reconduction'] || '',
          paymentStatusFilter: this.filterValues['payment'] || '',
          startDate: this.filterValues['startDate'] || null,
          endDate: this.filterValues['endDate'] || null
        });
        
        this.$emit('filtersChanged', contractFilters);
      } else {
        const simulationFilters = buildSqlFilters({
          selectedFilters: this.filterValues['status'] || [],
          selectedUsers: this.filterValues['users'] || [],
          referenceFilter: this.filterValues['reference'] || '',
          identifierFilter: this.filterValues['identifier'] || '',
          contractFilter: this.filterValues['contract'] || [],
          dateRange: this.filterValues['date'] || null,
          removeSimulation: this.filterValues['removeSimulation'] !== undefined ? this.filterValues['removeSimulation'] : true
        });
        
        this.$emit('filtersChanged', simulationFilters);
      }
    },
    debouncedEmitFilters() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.emitFilters();
      }, 1000);
    }
  },
  watch: {
    filterValues: {
      handler(newValues) {
        if (!this.filters) return;
        
        const statusFilter = this.filters.find(f => f.type === 'multiselect' && f.code === 'status');
        if (statusFilter && newValues[statusFilter.code]) {
          const encodedFilter = newValues[statusFilter.code].join(' or ');
          history.pushState({}, null,
            newValues[statusFilter.code].length ? `?filter=${encodedFilter}` : this.$route.path
          );
        }

        const textInputs = this.filters.filter(f => f.type === 'input').map(f => f.code);
        const changedKeys = Object.keys(newValues).filter(key => textInputs.includes(key));

        if (changedKeys.length > 0) {
          this.debouncedEmitFilters();
        } else {
          this.emitFilters();
        }
      },
      deep: true
    }
  },
  beforeUnmount() {
    if(this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
};
</script>

<style scoped>
.filters-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-label {
  color: #ffffff;
  font-size: 1em;
  font-weight: 600;
}
</style>